import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { toast } from "sonner";
import { adminSessionStatus } from "@/lib/admin.functions";
import {
  getDiscountRedemptions,
  type AdminRedemption,
  createDiscountCode,
  deleteDiscountCode,
  listDiscountCodes,
  toggleDiscountCode,
} from "@/lib/discount.functions";
import type { AdminDiscount } from "@/lib/discount.types";
import { BILLING_PLAN_CODES, formatBillingPrice, type BillingPlan } from "@/lib/billing.types";
import { billingPlanName } from "@/lib/billing.catalog";
import { getPublicBillingPlans } from "@/lib/billing.functions";
import { Button, buttonVariants } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export const Route = createFileRoute("/admin-rabattcodes")({
  staticData: { sitemap: false, toasts: true },
  ssr: false,
  component: DiscountAdmin,
  head: () => ({
    meta: [
      { title: "Begner Admin · Rabattcodes" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
});
function generatedCode() {
  const bytes = crypto.getRandomValues(new Uint8Array(10));
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  return `BEGNER-${Array.from(bytes, (b) => chars[b % 32]).join("")}`;
}
function swissDate(value: string, end = false) {
  const date = new Date(`${value}T00:00:00Z`);
  if (end) date.setUTCDate(date.getUTCDate() + 1);
  const offset = new Intl.DateTimeFormat("en", {
    timeZone: "Europe/Zurich",
    timeZoneName: "shortOffset",
  })
    .formatToParts(date)
    .find((p) => p.type === "timeZoneName")?.value;
  const hours = Number(offset?.replace("GMT", "") || 0);
  return new Date(date.getTime() - hours * 3600000).toISOString();
}
function DiscountAdmin() {
  const checkSession = useServerFn(adminSessionStatus);
  const getRedemptions = useServerFn(getDiscountRedemptions);
  const [history, setHistory] = useState<{ code: string; rows: AdminRedemption[] } | null>(null);
  const list = useServerFn(listDiscountCodes);
  const create = useServerFn(createDiscountCode);
  const toggle = useServerFn(toggleDiscountCode);
  const remove = useServerFn(deleteDiscountCode);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const getPlans = useServerFn(getPublicBillingPlans);
  const [authenticated, setAuthenticated] = useState<boolean | null>(null);
  const [items, setItems] = useState<AdminDiscount[]>([]);
  const [plans, setPlans] = useState<BillingPlan[]>([]);
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState("");
  const [percent, setPercent] = useState(60);
  const [duration, setDuration] = useState<"once" | "days" | "months">("months");
  const [value, setValue] = useState(3);
  const [selected, setSelected] = useState<string[]>([...BILLING_PLAN_CODES]);
  const [audience, setAudience] = useState("all");
  const load = useCallback(async () => {
    setItems(await list());
  }, [list]);
  useEffect(() => {
    void checkSession()
      .then(async (s) => {
        setAuthenticated(s.authenticated);
        if (s.authenticated) {
          await load();
          setPlans((await getPlans()).plans);
          setCode(generatedCode());
        }
      })
      .catch((e) => toast.error(e.message));
  }, [checkSession, getPlans, load]);
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    setBusy(true);
    try {
      await create({
        data: {
          code,
          name: String(data.get("name")),
          percent,
          durationType: duration,
          durationValue: duration === "once" ? 1 : value,
          planCodes: BILLING_PLAN_CODES.filter((c) => selected.includes(c)),
          audience: audience as "all" | "new" | "existing" | "personal",
          customerEmail: audience === "personal" ? String(data.get("email")) : undefined,
          startsAt: swissDate(String(data.get("start"))),
          expiresAt: data.get("end") ? swissDate(String(data.get("end")), true) : null,
          maxRedemptions: data.get("limit") ? Number(data.get("limit")) : null,
          perCustomerLimit: Number(data.get("perCustomer")),
        },
      });
      await load();
      setCode(generatedCode());
      toast.success("Rabattcode erstellt.");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Code konnte nicht erstellt werden.");
    } finally {
      setBusy(false);
    }
  }
  if (authenticated === null) return <div className="container-page py-12">Lade Rabattcodes …</div>;
  if (!authenticated)
    return (
      <div className="container-page py-12">
        <p>Bitte zuerst im Adminbereich anmelden.</p>
        <Link to="/admin">Zum Adminbereich</Link>
      </div>
    );
  return (
    <main className="container-page space-y-6 py-10">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-3xl font-bold">Rabattcodes</h1>
        <div className="flex gap-2">
          <Button asChild variant="outline">
            <Link to="/admin">Admin</Link>
          </Button>
          <Button asChild variant="outline">
            <Link to="/admin-rechnungen">Rechnungen</Link>
          </Button>
        </div>
      </div>
      <Card>
        <CardHeader>
          <CardTitle>Rabattcode erstellen</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-5">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="coupon-code">Code</Label>
                <div className="flex gap-2">
                  <Input
                    id="coupon-code"
                    required
                    value={code}
                    maxLength={40}
                    pattern="[A-Z0-9][A-Z0-9-]{2,39}"
                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                  />
                  <Button type="button" variant="outline" onClick={() => setCode(generatedCode())}>
                    Generieren
                  </Button>
                </div>
              </div>
              <Field label="Interner Name" name="name" required maxLength={120} />
              <div className="space-y-2">
                <Label htmlFor="coupon-percent">Rabatt in %</Label>
                <Input
                  id="coupon-percent"
                  type="number"
                  required
                  min={1}
                  max={100}
                  value={percent}
                  onChange={(e) => setPercent(Number(e.target.value))}
                />
              </div>
              <Field
                label="Einlösbar ab (Schweizer Zeit)"
                name="start"
                type="date"
                required
                defaultValue={new Intl.DateTimeFormat("en-CA", {
                  timeZone: "Europe/Zurich",
                  year: "numeric",
                  month: "2-digit",
                  day: "2-digit",
                }).format(new Date())}
              />
              <Field label="Einlösbar bis einschliesslich (optional)" name="end" type="date" />
              <div className="space-y-2">
                <Label htmlFor="coupon-duration">Rabattdauer ab Leistungsbeginn (Abos)</Label>
                <select
                  id="coupon-duration"
                  className="h-10 w-full rounded-md border bg-background px-3"
                  value={duration}
                  onChange={(e) => setDuration(e.target.value as typeof duration)}
                >
                  <option value="once">Erste Rechnung</option>
                  <option value="days">Tage</option>
                  <option value="months">Monatsrechnungen</option>
                </select>
              </div>
              {duration !== "once" ? (
                <div className="space-y-2">
                  <Label htmlFor="coupon-value">
                    Anzahl {duration === "days" ? "Tage" : "Monatsrechnungen"}
                  </Label>
                  <Input
                    id="coupon-value"
                    type="number"
                    required
                    min={1}
                    max={duration === "months" ? 120 : 3650}
                    value={value}
                    onChange={(e) => setValue(Number(e.target.value))}
                  />
                </div>
              ) : null}
              <Field
                label="Maximale Einlösungen (leer = unbegrenzt)"
                name="limit"
                type="number"
                min={1}
              />
              <Field
                label="Einlösungen pro Kunde"
                name="perCustomer"
                type="number"
                required
                min={1}
                max={100}
                defaultValue={1}
              />
              <div className="space-y-2">
                <Label htmlFor="coupon-audience">Zielgruppe</Label>
                <select
                  id="coupon-audience"
                  className="h-10 w-full rounded-md border bg-background px-3"
                  value={audience}
                  onChange={(e) => setAudience(e.target.value)}
                >
                  <option value="all">Alle berechtigten Kunden</option>
                  <option value="new">Nur Neukunden</option>
                  <option value="existing">Nur Bestandskunden</option>
                  <option value="personal">Bestimmte bestätigte E-Mail</option>
                </select>
              </div>
              {audience === "personal" ? (
                <Field label="Bestätigte Kunden-E-Mail" name="email" type="email" required />
              ) : null}
            </div>
            <fieldset className="flex flex-wrap gap-4">
              <legend className="mb-2 font-semibold">Gültige Pakete</legend>
              {BILLING_PLAN_CODES.map((c) => (
                <label key={c} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={selected.includes(c)}
                    onChange={(e) =>
                      setSelected((previous) =>
                        e.target.checked ? [...previous, c] : previous.filter((x) => x !== c),
                      )
                    }
                  />
                  {billingPlanName(c, "de")}
                </label>
              ))}
            </fieldset>
            <div className="rounded-xl bg-muted/40 p-4 text-sm">
              <p className="mb-2 font-semibold">
                Preisvorschau · keine Kombination mit öffentlichen Aktionen
              </p>
              {plans
                .filter((p) => selected.includes(p.code))
                .map((p) => {
                  const days =
                    duration === "days" && p.billingInterval === "month"
                      ? Math.min(value, 30) / 30
                      : 1;
                  const amount =
                    p.priceMinor -
                    Math.round(
                      Math.max(0, p.priceMinor - (p.regularPriceMinor * (100 - percent)) / 100) *
                        days,
                    );
                  return (
                    <p key={p.code}>
                      {billingPlanName(p.code, "de")}: {formatBillingPrice(p.priceMinor, "de-CH")} →{" "}
                      <strong>{formatBillingPrice(amount, "de-CH")}</strong>
                      {amount >= p.priceMinor
                        ? " · aktuelle Aktion ist günstiger oder gleich günstig"
                        : ""}
                    </p>
                  );
                })}
              <p className="mt-2 text-muted-foreground">
                Tagesvorschau mit 30 Tagen; die Bestellung berechnet den tatsächlichen
                Leistungsmonat. Inserate erhalten den Rabatt einmalig. Nach Rabattende gilt der
                aktuelle Paketpreis.
              </p>
            </div>
            <Button disabled={busy || !selected.length} type="submit">
              {busy ? "Wird erstellt …" : "Rabattcode erstellen"}
            </Button>
          </form>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Erstellte Codes</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {!items.length ? (
            <p>Noch keine Codes vorhanden.</p>
          ) : (
            items.map((c) => (
              <div
                key={c.id}
                className="flex flex-wrap items-center justify-between gap-4 rounded-xl border p-4"
              >
                <div>
                  <p className="font-semibold">
                    {c.code} · {c.percent} %{" "}
                    <span className="text-muted-foreground">
                      {!c.active
                        ? "· deaktiviert"
                        : c.expires_at && new Date(c.expires_at) <= new Date()
                          ? "· abgelaufen"
                          : new Date(c.starts_at) > new Date()
                            ? "· geplant"
                            : "· aktiv"}
                    </span>
                  </p>
                  <p className="text-sm">
                    {c.name} ·{" "}
                    {c.duration_type === "once"
                      ? "erste Rechnung"
                      : `${c.duration_value} ${c.duration_type === "days" ? "Tage" : "Monatsrechnungen"}`}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {c.plan_codes.map((p) => billingPlanName(p, "de")).join(", ")}
                  </p>
                  <p className="text-sm">
                    {c.reserved} reserviert · {c.used} eingelöst / {c.max_redemptions ?? "∞"} ·{" "}
                    {c.per_customer_limit} pro Kunde ·{" "}
                    {formatBillingPrice(c.savings_minor, "de-CH")} Rabatt gewährt
                  </p>
                  <p className="text-sm">
                    Einlösbar ab{" "}
                    {new Date(c.starts_at).toLocaleString("de-CH", { timeZone: "Europe/Zurich" })}
                    {c.expires_at
                      ? ` bis ${new Date(new Date(c.expires_at).getTime() - 1000).toLocaleString("de-CH", { timeZone: "Europe/Zurich" })}`
                      : " · ohne Enddatum"}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    onClick={() =>
                      void navigator.clipboard
                        .writeText(c.code)
                        .then(() => toast.success("Code kopiert."))
                        .catch(() => toast.error("Kopieren nicht möglich."))
                    }
                  >
                    Kopieren
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setCode(generatedCode());
                      setPercent(c.percent);
                      setDuration(c.duration_type);
                      setValue(c.duration_value);
                      setSelected(c.plan_codes);
                      setAudience(c.audience);
                      window.scrollTo({ top: 0, behavior: "smooth" });
                      toast.info(
                        "Rabatt und Pakete übernommen. Bitte Name und Gültigkeit eingeben.",
                      );
                    }}
                  >
                    Als Vorlage
                  </Button>
                  <Button
                    variant="outline"
                    onClick={async () => {
                      try {
                        setHistory({
                          code: c.code,
                          rows: await getRedemptions({ data: { id: c.id } }),
                        });
                      } catch (error) {
                        toast.error(error instanceof Error ? error.message : "Fehler");
                      }
                    }}
                  >
                    Einlösungen
                  </Button>
                  <Button
                    variant="outline"
                    disabled={busy}
                    onClick={async () => {
                      setBusy(true);
                      try {
                        await toggle({ data: { id: c.id, active: !c.active } });
                        await load();
                      } catch (e) {
                        toast.error(e instanceof Error ? e.message : "Fehler");
                      } finally {
                        setBusy(false);
                      }
                    }}
                  >
                    {c.active ? "Deaktivieren" : "Aktivieren"}
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="destructive" disabled={busy}>
                        Löschen
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Rabattcode {c.code} löschen?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Der Code wird aus der Liste entfernt und kann nicht mehr eingelöst werden.
                          Bereits zugesagte Rabatte und bestehende Rechnungen bleiben erhalten. Der
                          gleiche Code kann danach nicht erneut erstellt werden.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel disabled={busy}>Abbrechen</AlertDialogCancel>
                        <AlertDialogAction
                          className={buttonVariants({ variant: "destructive" })}
                          disabled={busy}
                          onClick={async (event) => {
                            event.preventDefault();
                            setBusy(true);
                            setDeletingId(c.id);
                            try {
                              await remove({ data: { id: c.id } });
                              setItems((current) => current.filter((item) => item.id !== c.id));
                              setHistory((current) => (current?.code === c.code ? null : current));
                              toast.success("Rabattcode gelöscht.");
                            } catch (error) {
                              toast.error(
                                error instanceof Error
                                  ? error.message
                                  : "Rabattcode konnte nicht gelöscht werden.",
                              );
                            } finally {
                              setBusy(false);
                              setDeletingId(null);
                            }
                          }}
                        >
                          {deletingId === c.id ? "Wird gelöscht …" : "Code löschen"}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>
      {history ? (
        <Card>
          <CardHeader>
            <CardTitle>Einlösungen: {history.code}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <p className="text-sm text-muted-foreground">
              Letzte 100 Einlösungen · Zeitangaben in Europe/Zurich
            </p>
            {history.rows.length ? (
              history.rows.map((r) => (
                <div key={r.id} className="rounded-lg border p-3 text-sm">
                  <p className="font-semibold">
                    {r.customer_email} · {r.status}
                  </p>
                  <p>
                    {new Date(r.created_at).toLocaleString("de-CH", { timeZone: "Europe/Zurich" })}{" "}
                    · {r.invoice_number ?? "Noch keine Rechnung"} ·{" "}
                    {r.amount_minor !== null ? formatBillingPrice(r.amount_minor, "de-CH") : ""}
                  </p>
                  <a href="/admin-rechnungen" className="text-primary underline">
                    Rechnungen öffnen
                  </a>
                </div>
              ))
            ) : (
              <p>Noch keine Einlösungen.</p>
            )}
          </CardContent>
        </Card>
      ) : null}
    </main>
  );
}
function Field({
  label,
  name,
  ...props
}: { label: string; name: string } & React.ComponentProps<typeof Input>) {
  return (
    <div className="space-y-2">
      <Label htmlFor={`coupon-${name}`}>{label}</Label>
      <Input id={`coupon-${name}`} name={name} {...props} />
    </div>
  );
}
